import React, { useContext, useMemo } from 'react';
import { ProductMasterDataContext } from '../../../../../../../context/ProductMasterDataContext';
import { BinConstraintInput } from '../../../../../../../types/bin_constraints/binConstraint.input';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../../types/translation/Translator';
import { DefaultDropdownItem, DropdownSearchSelect } from '../../../../../../components/Dropdown';
import { BinContext } from '../../../../../../../context/BinContext';
import { StockLocationContext } from '../../../../../../../context/StockLocationContext';
import { BinConstraintContext } from '../../../../../../../context/BinConstraintContext';

interface BinConstraintInfoPaneProps {
  input: BinConstraintInput;
  setInput: (bc: BinConstraintInput) => void;
}

export default function BinConstraintInfoPane({ input, setInput }: BinConstraintInfoPaneProps) {
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { bins } = useContext(BinContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { binConstraints } = useContext(BinConstraintContext);

  const alreadyInUseProductMasterDataIds = useMemo(() => {
    return new Set(
      Array.from(binConstraints.values())
        .filter(bc => bc.stockLocationId === input.stockLocationId)
        .map(bc => bc.pmdId),
    );
  }, [input.stockLocationId]);

  const alreadyInUseBins = useMemo(() => {
    return new Set(
      Array.from(binConstraints.values())
        .filter(bc => bc.stockLocationId === input.stockLocationId && bc.pmdId === input.pmdId && bc.id !== input.id)
        .map(bc => bc.binId),
    );
  }, [input.stockLocationId, input.pmdId]);

  return (
    <div className='grid min-w-[400px] gap-4'>
      <DropdownSearchSelect
        mandatory
        label={t(TranslationKey.stockLocation).singular.upper}
        value={stockLocations.get(input.stockLocationId)}
        items={[...stockLocations.values()]}
        onSelect={value => setInput(input.withStockLocationId(value?.id))}
        placeholder={t(TranslationKey.selectStockLocation).singular.label}
        textBuilder={sl => sl.name}
        itemBuilder={sl => <DefaultDropdownItem text={sl.name} />}
        searchBuilder={sl => `${sl.name}`}
        disabled={!!input.id}
      />
      {input.stockLocationId ? (
        <DropdownSearchSelect
          mandatory
          label={t(TranslationKey.productMasterData).singular.upper}
          value={productMasterData.get(input.pmdId)}
          items={[...productMasterData.values()].filter(pmd => !alreadyInUseProductMasterDataIds.has(pmd.id))}
          onSelect={value => setInput(input.withPmdId(value?.id))}
          placeholder={t(TranslationKey.selectProductReference).singular.label}
          textBuilder={pmd => pmd.productName}
          itemBuilder={pmd => <DefaultDropdownItem text={pmd.productName} subtext={pmd.productNumber} />}
          searchBuilder={pmd => `${pmd.productName} - ${pmd.productNumber}`}
          disabled={!!input.id}
        />
      ) : null}
      {input.pmdId ? (
        <DropdownSearchSelect
          mandatory
          label={t(TranslationKey.bin).singular.upper}
          value={bins.get(input.binId)}
          items={[...bins.values()].filter(
            bin => !alreadyInUseBins.has(bin.id) && bin.stockLocationId === input.stockLocationId,
          )}
          onSelect={value => setInput(input.withBinId(value?.id))}
          placeholder={t(TranslationKey.selectBin).singular.label}
          textBuilder={bin => bin.name}
          itemBuilder={bin => <DefaultDropdownItem text={bin.name} />}
          searchBuilder={bin => `${bin.name}`}
        />
      ) : null}
    </div>
  );
}
